import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '../../../common/hooks/useThunkDispatch';
import { featureFlagsSelectors } from '../../../common/selectors/featureFlagsSelectors';
import { ensureLeadingSlash, trimEnd } from '../../../common/utils/stringUtils';

const normalizePath = (path?: string) =>
    path ? ensureLeadingSlash(trimEnd(path, '/')) : undefined;

const useSimpleAddressRedirect = (params: { pageUrl?: string; simpleAddressUrl?: string }) => {
    const { pathname } = useLocation();
    const isSimpleAddressRedirectEnabled = useAppSelector((state) =>
        featureFlagsSelectors.selectDynamicFlagEnabled(state, 'EnableSimpleAddressRedirect'),
    );

    return useMemo(() => {
        if (!isSimpleAddressRedirectEnabled || !params.pageUrl || !params.simpleAddressUrl) {
            return undefined;
        }

        const targetPagePath = normalizePath(
            new URL(params.pageUrl, window.location.origin).pathname,
        );
        const simpleAddressPath = normalizePath(
            new URL(params.simpleAddressUrl, window.location.origin).pathname,
        );
        const currentPath = normalizePath(pathname);

        if (!targetPagePath) {
            throw new Error(`Target page has invalid url: ${params.pageUrl}`);
        }

        if (currentPath === simpleAddressPath) {
            return targetPagePath;
        }

        return undefined;
    }, [isSimpleAddressRedirectEnabled, params.pageUrl, params.simpleAddressUrl, pathname]);
};

export default useSimpleAddressRedirect;
